/**
 * useOrders Hook
 * Admin/subscriber orders fetch + status mutation.
 */

import { useCallback, useEffect, useState } from 'react';
import { ordersAPI, Order, OrderStatus } from '../services/api';

export interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateStatus: (orderId: string, status: OrderStatus, trackingNumber?: string) => Promise<boolean>;
}

export function useOrders(): UseOrdersReturn {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ordersAPI.getAll();
      setOrders(res.orders || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
    // Poll every 30s so a new paid order surfaces in admin without a reload.
    const id = setInterval(() => { refresh().catch(() => {}); }, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const updateStatus = useCallback(async (
    orderId: string,
    status: OrderStatus,
    trackingNumber?: string,
  ): Promise<boolean> => {
    try {
      const res = await ordersAPI.updateStatus(orderId, status, trackingNumber);
      if (res?.success) {
        // Optimistic local update — keeps the row in place with new pill colour.
        setOrders(prev => prev.map(o =>
          o.id === orderId
            ? { ...o, status, tracking_number: trackingNumber ?? o.tracking_number }
            : o
        ));
        return true;
      }
      return false;
    } catch (e) {
      console.error('updateStatus failed', e);
      return false;
    }
  }, []);

  return { orders, loading, error, refresh, updateStatus };
}
